import { IconButton, Stack, Tooltip, useTheme } from "@mui/material";
import {
  PlusIcon,
  SearchIcon,
  TwoChatBubbleIcon,
  WindowResizeRightIcon,
} from "components/icons";
import { useAtom, useSetAtom } from "jotai";
import { HelenaSectionIcon } from "shared/features/helena/components/HelenaSectionIcon";
import { HelenaLogo } from "shared/features/helena/components/icons";
import { isChatSearchOpenAtom, isChatSidebarOpenAtom } from "../store";

/** Kept in sync with the collapsed width the layout animates to. */
export const CHAT_SIDEBAR_RAIL_WIDTH = "56px";

interface ChatSidebarRailProps {
  onNewChat: () => void;
}

/**
 * The collapsed sidebar: the same actions as {@link ChatSidebar}, reduced to a
 * narrow icon rail. It keeps the chat list one click away instead of hiding the
 * entry points inside the panel.
 */
export const ChatSidebarRail = ({ onNewChat }: ChatSidebarRailProps) => {
  const theme = useTheme();
  const setIsSidebarOpen = useSetAtom(isChatSidebarOpenAtom);
  const [isSearchOpen, setIsSearchOpen] = useAtom(isChatSearchOpenAtom);

  const buttonSx = {
    p: "6px",
    borderRadius: "4px",
    color: theme.palette.lightBg.medium,
    ":hover": { backgroundColor: theme.palette.neutral["005"] },
  };

  return (
    <Stack
      sx={{
        // Holds its own width inside the animating wrapper, so the icons stay
        // put while the rail is revealed instead of reflowing.
        width: CHAT_SIDEBAR_RAIL_WIDTH,
        height: "100%",
        flexShrink: 0,
        minHeight: 0,
        alignItems: "center",
        gap: "12px",
        p: "12px 8px",
      }}
    >
      {/* The logo slot doubles as the expand control: the panel-toggle glyph
          takes its place on hover, so the rail keeps one 32px top slot. */}
      <Tooltip title="Show chat list" placement="right">
        <Stack
          component="button"
          type="button"
          aria-label="Show chat list"
          onClick={() => setIsSidebarOpen(true)}
          sx={{
            width: "32px",
            height: "32px",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
            border: "none",
            borderRadius: "8px",
            backgroundColor: "transparent",
            cursor: "pointer",
            ":hover .rail-logo": { display: "none" },
            ":hover .rail-toggle": { display: "inline-block" },
          }}
        >
          <Stack className="rail-logo">
            <HelenaSectionIcon icon={<HelenaLogo />} />
          </Stack>
          <WindowResizeRightIcon
            className="rail-toggle"
            sx={{
              display: "none",
              fontSize: "20px",
              color: theme.palette.lightBg.high,
            }}
          />
        </Stack>
      </Tooltip>

      <Stack sx={{ alignItems: "center", gap: "4px", pt: "12px" }}>
        <Tooltip title="New chat" placement="right">
          <IconButton aria-label="New chat" onClick={onNewChat} sx={buttonSx}>
            <PlusIcon sx={{ fontSize: "24px" }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Search chat" placement="right">
          <IconButton
            aria-label="Search chat"
            onClick={() => setIsSearchOpen(true)}
            sx={{
              ...buttonSx,
              backgroundColor: isSearchOpen
                ? theme.palette.neutral["010"]
                : "transparent",
            }}
          >
            <SearchIcon sx={{ fontSize: "24px" }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Chat history" placement="right">
          <IconButton
            aria-label="Chat history"
            onClick={() => setIsSidebarOpen(true)}
            sx={buttonSx}
          >
            <TwoChatBubbleIcon sx={{ fontSize: "24px" }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
